import { api } from './api';

export type FeeNoteStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'sending'           // email dispatch in progress
  | 'sent'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'cancelled'
  | 'written_off'
  | 'send_failed'       // email dispatch failed; clerk must retry
  | 'partially_credited'
  | 'credited';

export interface FeeNoteListRow {
  id: string;
  chambersId: string;
  caseId: string;
  feeNoteNumber: string;
  status: FeeNoteStatus;
  issueDate: string;
  dueDate: string;
  subtotal: string;
  vatTotal: string;
  total: string;
  amountPaid: string;
  writtenOffAmount: string;
  creditedAmount: string;
  refundedAmount: string;
  currency: string;
  pdfUrl: string | null;
  sentAt: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
  case: { id: string; caseNumber: string; caseName: string } | null;
  /** Server-authoritative outstanding balance (refund-aware). READ this — never recompute. */
  outstanding: number;
}

export interface FeeNoteDetail extends FeeNoteListRow {
  notes: string | null;
  case: {
    id: string;
    caseNumber: string;
    caseName: string;
    instructingSolicitor: {
      id: string;
      firstName: string | null;
      lastName: string | null;
      firmName: string | null;
      company: string | null;
      email: string | null;
      billingEmail: string | null;
    } | null;
  } | null;
  feeRecords: Array<{
    id: string;
    description: string;
    feeDate: string;
    durationMinutes: number | null;
    rate: string | null;
    amount: string;
    vatAmount: string;
    totalAmount: string;
    barrister: {
      id: string;
      user: { firstName: string; lastName: string };
    } | null;
  }>;
}

export interface ListFeeNotesQuery {
  caseId?: string;
  barristerId?: string;
  status?: FeeNoteStatus;
  page?: number;
  limit?: number;
}

export interface AgedDebtRow {
  contactId: string | null;
  clientName: string;
  mattersCount: number;
  current: number;
  d30: number;
  d60: number;
  d90: number;
  total: number;
}

export async function fetchAgedDebt(): Promise<AgedDebtRow[]> {
  const res = await api.get<{ data: AgedDebtRow[] }>('/fee-notes/aged-debt');
  return res.data.data ?? [];
}

export interface RecoveryWorkspaceItem {
  feeNoteId: string;
  feeNoteNumber: string;
  caseId: string;
  caseName: string;
  caseNumber: string;
  clientName: string;
  contactId: string | null;
  total: number;
  outstanding: number;
  issueDate: string;
  dueDate: string;
  sentAt: string | null;
  status: string;
  daysOverdue: number;
  lastChased: {
    id: string;
    contactedAt: string;
    method: string;
    notes: string | null;
    daysSince: number;
    loggedBy: { firstName: string; lastName: string };
  } | null;
  chaseCount: number;
}

export async function fetchRecoveryWorkspace(): Promise<RecoveryWorkspaceItem[]> {
  const res = await api.get<{ data: RecoveryWorkspaceItem[] }>('/fee-notes/recovery-workspace');
  return res.data.data ?? [];
}

export interface ListFeeNotesResponse {
  feeNotes: FeeNoteListRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchFeeNotes(query: ListFeeNotesQuery = {}): Promise<ListFeeNotesResponse> {
  const res = await api.get<ListFeeNotesResponse>('/fee-notes', { params: query });
  return res.data;
}

export async function fetchFeeNote(id: string): Promise<FeeNoteDetail> {
  const res = await api.get<{ feeNote: FeeNoteDetail }>(`/fee-notes/${id}`);
  return res.data.feeNote;
}

export interface CreateFeeNotePayload {
  caseId: string;
  feeRecordIds: string[];
  issueDate?: string;
  dueDate?: string;
  notes?: string;
}

export async function createFeeNote(payload: CreateFeeNotePayload): Promise<FeeNoteDetail> {
  const res = await api.post<{ feeNote: FeeNoteDetail }>('/fee-notes', payload);
  return res.data.feeNote;
}

export async function generateFeeNotePdf(id: string): Promise<{ feeNote: FeeNoteDetail; sizeBytes: number }> {
  const res = await api.post<{ feeNote: FeeNoteDetail; sizeBytes: number }>(`/fee-notes/${id}/generate-pdf`);
  return res.data;
}

export async function fetchFeeNoteDownloadUrl(id: string): Promise<{ url: string; feeNoteNumber: string }> {
  const res = await api.get<{ url: string; feeNoteNumber: string }>(`/fee-notes/${id}/download-url`);
  return res.data;
}

export interface SendFeeNotePayload {
  toEmail?: string;
  ccEmails?: string[];
  message?: string;
}

export async function sendFeeNote(id: string, payload: SendFeeNotePayload = {}): Promise<FeeNoteDetail> {
  const res = await api.post<{ feeNote: FeeNoteDetail }>(`/fee-notes/${id}/send`, payload);
  return res.data.feeNote;
}

/** Barrister (or admin) approves a clerk-drafted fee note: pending_approval → approved. */
export async function approveFeeNote(id: string): Promise<FeeNoteDetail> {
  const res = await api.post<{ feeNote: FeeNoteDetail }>(`/fee-notes/${id}/approve`);
  return res.data.feeNote;
}

/** Barrister (or admin) returns a drafted fee note for amendment: pending_approval → draft. */
export async function rejectFeeNote(id: string, reason: string): Promise<FeeNoteDetail> {
  const res = await api.post<{ feeNote: FeeNoteDetail }>(`/fee-notes/${id}/reject`, { reason });
  return res.data.feeNote;
}

/** Cancel a fee note raised in error (no money against it) → frees its records to re-bill. */
export async function cancelFeeNote(id: string, reason: string): Promise<FeeNoteDetail> {
  const res = await api.post<{ feeNote: FeeNoteDetail }>(`/fee-notes/${id}/cancel`, { reason });
  return res.data.feeNote;
}

/** Record a refund — cash actually returned to the payer (nets down collections; not a void). */
export async function refundFeeNote(id: string, amount: number, reason: string): Promise<FeeNoteDetail> {
  const res = await api.post<{ feeNote: FeeNoteDetail }>(`/fee-notes/${id}/refund`, { amount, reason });
  return res.data.feeNote;
}

/** Emails the instructing solicitor a payment reminder for an already-sent, unpaid fee note. */
export async function sendFeeNoteReminder(id: string, message?: string): Promise<FeeNoteDetail> {
  const res = await api.post<{ data: FeeNoteDetail }>(`/fee-notes/${id}/reminder`, message ? { message } : {});
  return res.data.data;
}

// ─── Chase log ───────────────────────────────────────────────

export interface ChaseLog {
  id: string;
  feeNoteId: string;
  method: string;
  notes: string | null;
  contactedAt: string;
  createdAt: string;
  loggedBy: { id: string; firstName: string; lastName: string; memberType: string };
}

export async function fetchChaseLogs(feeNoteId: string): Promise<ChaseLog[]> {
  const res = await api.get<{ data: ChaseLog[] }>(`/fee-notes/${feeNoteId}/chase-log`);
  return res.data.data;
}

export async function addChaseLog(
  feeNoteId: string,
  payload: { method: string; notes?: string; contactedAt: string },
): Promise<ChaseLog> {
  const res = await api.post<{ data: ChaseLog }>(`/fee-notes/${feeNoteId}/chase-log`, payload);
  return res.data.data;
}

// ─── Stats (server-calculated) ──────────────────────────────────────────────

export interface FeeNoteStats {
  outstanding: number;
  outstandingCount: number;
  overdue: number;
  overdueCount: number;
  totalBilled: number;
  totalCollected: number;
  collectionRate: number;
}

export async function fetchFeeNoteStats(): Promise<FeeNoteStats> {
  const res = await api.get<{ data: FeeNoteStats }>('/fee-notes/stats');
  return res.data.data;
}

export const STATUS_LABELS: Record<FeeNoteStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  sending: 'Sending…',
  sent: 'Sent',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  written_off: 'Written off',
  send_failed: 'Send failed',
  partially_credited: 'Partially credited',
  credited: 'Credited',
};

// ─── Billing validation ─────────────────────────────────────

export interface BillingValidationError {
  code: string;
  message: string;
  severity: 'blocking' | 'warning';
  field?: string;
}

export interface BillingValidationResult {
  valid: boolean;
  errors: BillingValidationError[];
}

export async function validateFeeNote(payload: {
  caseId: string;
  feeRecordIds: string[];
}): Promise<BillingValidationResult> {
  const res = await api.post<{ data: BillingValidationResult }>(
    '/fee-notes/validate',
    payload,
  );
  return res.data.data;
}
